const NCA_LAYER_URL = "wss://127.0.0.1:13579/";
const SIGNATURE_EXT_KEY_USAGE_OID = "1.3.6.1.5.5.7.3.4";
const AUTH_EXT_KEY_USAGE_OID = "1.3.6.1.5.5.7.3.2";
const NCA_LAYER_TIMEOUT = 30000;

type NcaLayerSignOptions = {
  extKeyUsageOids?: string[];
};

type NcaLayerRequest = Record<string, unknown>;

export async function createCmsSignatureFromBase64(
  base64Data: string,
  options: NcaLayerSignOptions = {}
): Promise<string> {
  const extKeyUsageOids =
    options.extKeyUsageOids ?? [SIGNATURE_EXT_KEY_USAGE_OID];

  const basicsRequest: NcaLayerRequest = {
    module: "kz.gov.pki.knca.basics",
    method: "sign",
    args: {
      allowedStorages: ["PKCS12"],
      format: "cms",
      data: base64Data,
      signingParams: {
        decode: true,
        encapsulate: true,
        digested: false,
        tsaProfile: {},
      },
      signerParams: {
        extKeyUsageOids,
      },
      locale: "ru",
    },
  };

  try {
    return await executeSignRequest(basicsRequest);
  } catch (error) {
    if (!shouldFallbackToLegacy(error)) {
      throw error;
    }

    const legacyKeyType = extKeyUsageOids.includes(AUTH_EXT_KEY_USAGE_OID)
      ? "AUTHENTICATION"
      : "SIGNATURE";
    const legacyRequest: NcaLayerRequest = {
      module: "kz.gov.pki.knca.commonUtils",
      method: "createCMSSignatureFromBase64",
      args: ["PKCS12", legacyKeyType, base64Data, true],
    };
    return executeSignRequest(legacyRequest);
  }
}

export function createAuthCmsSignatureFromBase64(
  base64Data: string
): Promise<string> {
  return createCmsSignatureFromBase64(base64Data, {
    extKeyUsageOids: [AUTH_EXT_KEY_USAGE_OID],
  });
}

export async function checkNcaLayerConnection(
  timeoutMs = 3000
): Promise<boolean> {
  return new Promise((resolve) => {
    let socket: WebSocket | null = null;
    let finished = false;

    const finish = (result: boolean) => {
      if (finished) {
        return;
      }
      finished = true;
      window.clearTimeout(timer);
      if (
        socket &&
        (socket.readyState === WebSocket.OPEN ||
          socket.readyState === WebSocket.CONNECTING)
      ) {
        socket.close();
      }
      resolve(result);
    };

    const timer = window.setTimeout(() => finish(false), timeoutMs);

    try {
      socket = new WebSocket(NCA_LAYER_URL);
      socket.onopen = () => finish(true);
      socket.onerror = () => finish(false);
      socket.onclose = () => finish(false);
    } catch (error) {
      finish(false);
    }
  });
}

async function executeSignRequest(request: NcaLayerRequest): Promise<string> {
  return new Promise((resolve, reject) => {
    const socket = new WebSocket(NCA_LAYER_URL);
    let finished = false;

    const timer = window.setTimeout(() => {
      finish(() => {
        reject(new Error("NCALayer did not respond in time"));
      });
    }, NCA_LAYER_TIMEOUT);

    const cleanup = () => {
      window.clearTimeout(timer);
      if (
        socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING
      ) {
        socket.close();
      }
    };

    const finish = (callback: () => void) => {
      if (finished) {
        return;
      }
      finished = true;
      cleanup();
      callback();
    };

    socket.onopen = () => {
      socket.send(JSON.stringify(request));
    };

    socket.onmessage = (event) => {
      try {
        const response = JSON.parse(event.data);
        const cms = extractCms(response);
        if (cms) {
          finish(() => resolve(cms));
          return;
        }

        const errorMessage = extractErrorMessage(response);
        if (errorMessage) {
          finish(() => reject(new Error(errorMessage)));
          return;
        }
      } catch (error) {
        finish(() =>
          reject(
            error instanceof Error
              ? error
              : new Error("Failed to parse NCALayer response")
          )
        );
      }
    };

    socket.onerror = () => {
      finish(() => reject(new Error("NCA Layer connection failed")));
    };

    socket.onclose = (event) => {
      if (!finished && !event.wasClean) {
        finish(() =>
          reject(new Error(event.reason || "NCA Layer connection closed"))
        );
      }
    };
  });
}

function extractCms(response: unknown): string | null {
  const value = response as Record<string, any> | null;
  const candidates = [
    value?.responseObject,
    value?.result,
    value?.body,
    value?.body?.result,
    value?.responseObject?.result,
  ];

  for (const candidate of candidates) {
    const cms = normalizeCmsCandidate(candidate);
    if (cms) {
      return cms;
    }
  }

  return null;
}

function normalizeCmsCandidate(candidate: unknown): string | null {
  if (!candidate) {
    return null;
  }

  let value: any = Array.isArray(candidate) ? candidate[0] : candidate;

  if (typeof value === "object") {
    if (Array.isArray(value.result)) {
      value = value.result[0];
    } else if (typeof value.result === "string") {
      value = value.result;
    } else if (typeof value.responseObject === "string") {
      value = value.responseObject;
    } else {
      return null;
    }
  }

  if (typeof value !== "string") {
    return null;
  }

  const cleaned = value
    .replace(/-----BEGIN [^-]+-----/g, "")
    .replace(/-----END [^-]+-----/g, "")
    .replace(/\s+/g, "");

  return cleaned || null;
}

function extractErrorMessage(response: unknown): string | null {
  const value = response as Record<string, any> | null;
  if (!value) {
    return "Empty response from NCALayer";
  }

  if (value.code && value.code !== "200") {
    return value.message || "NCALayer returned an error";
  }

  const nestedMessage =
    value.message ||
    value.body?.message ||
    value.responseObject?.message ||
    value.result?.message;

  return typeof nestedMessage === "string" && nestedMessage.trim()
    ? nestedMessage.trim()
    : null;
}

function shouldFallbackToLegacy(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  return (
    message.includes("method not found") ||
    message.includes("module not found") ||
    message.includes("not supported") ||
    message.includes("unknown method") ||
    message.includes("unknown module")
  );
}

export async function base64ToFile(
  base64: string,
  filename: string,
  mimeType = "application/cms"
): Promise<File> {
  const dataUrl = `data:${mimeType};base64,${base64}`;
  const response = await fetch(dataUrl);
  const arrayBuffer = await response.arrayBuffer();
  return new File([arrayBuffer], filename, { type: mimeType });
}
